import { createSelector } from 'reselect';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { AppState } from '../../types';
import {
  createCSSTrie,
  NamedElementMatcher,
  namedElementsToSelectors,
  sortSelectorsBySpecificity,
} from '@fullstory/named-elements';
import { useRequestState } from '../../utils/hooks';
import { getIsReady } from '@fullstory/redux-util';

const noResult = new Map();
export const getNamedElementsById = (state: AppState): Map<string, NamedElementRecord> =>
  state.namedElements.namedElements ?? noResult;

export const getAllNamedElementsLoading = (state: AppState) =>
  state.namedElements.getAllNamedElements.loading;

export const getAllNamedElementsReady = (state: AppState) =>
  getIsReady(state.namedElements.getAllNamedElements);

export const useElementsRequestState = () =>
  useRequestState(state => state.namedElements.getAllNamedElements);

// This trie creation can take a while to complete on orgs with a large number of named elements.
// We want to periodically yield to the main thread so that the UI doesn't freeze for the entire process.
export const getNamedElementsTriePromise = createSelector(
  getNamedElementsById,
  async namedElementsById => {
    const trie = createCSSTrie<string>();
    const elements = Array.from(namedElementsById.values());

    function processElement(i: number) {
      return new Promise(resolve => {
        setTimeout(() => {
          const element = elements[i];
          if (element && element.namedElement) {
            element.namedElement.platformSelectors.forEach(ps => {
              trie.put(ps.selector.toLowerCase(), element.namedElement!.id);
            });
          }
          resolve(null);
        }, 0);
      });
    }

    for (let i = 0; i < elements.length; i++) {
      await processElement(i);
    }

    return trie;
  },
);
// Includes archived.
export const getAllNamedElements = createSelector(getNamedElementsById, byId => {
  const namedElements = [...byId.values()];
  namedElements.sort((a, b) => {
    const aName = a.namedElement?.name || '';
    const bName = b.namedElement?.name || '';
    return aName.localeCompare(bName);
  });
  return namedElements;
});

export const getUnarchivedNamedElementsByName = createSelector(getNamedElementsById, byId => {
  const byName = new Map<string, NamedElementRecord>();

  byId.forEach(namedElement => {
    if (!namedElement?.metadata?.archived && namedElement.namedElement) {
      byName.set(namedElement.namedElement.name, namedElement);
    }
  });

  return byName;
});

export const getUnarchivedNamedElementsBySelector = createSelector(getNamedElementsById, byId => {
  const bySelector = new Map<string, NamedElementRecord>();

  byId.forEach(namedElement => {
    if (!namedElement?.metadata?.archived && namedElement.namedElement) {
      namedElement.namedElement.platformSelectors.forEach(ps =>
        bySelector.set(ps.selector, namedElement),
      );
    }
  });

  return bySelector;
});

export const getUnarchivedNamedElementsById = createSelector(getNamedElementsById, byId => {
  const unarchivedById = new Map<string, NamedElementRecord>();

  byId.forEach(namedElement => {
    if (!namedElement?.metadata?.archived && namedElement.namedElement) {
      unarchivedById.set(namedElement.namedElement.id, namedElement);
    }
  });

  return unarchivedById;
});

export const getUnarchivedNamedElements = createSelector(getAllNamedElements, namedElements =>
  namedElements.filter(namedElement => !namedElement?.metadata?.archived),
);

// Provides unarchived named element selectors in most specific to least specific order.
// A named element could be present multiple times if they have multiple selectors that fall at different
// points in the specificity rankings.
export const getNamedElementSelectors = createSelector(
  getUnarchivedNamedElements,
  unarchivedNamedElements =>
    sortSelectorsBySpecificity(namedElementsToSelectors(unarchivedNamedElements)),
);

export const getNamedElementMatcher = createSelector(
  getNamedElementsById,
  recordsById => new NamedElementMatcher(recordsById),
);

export const getAllSuggestedElementsById = (state: AppState): Map<string, NamedElementRecord> =>
  state.namedElements.suggestedElements?.all ?? noResult;

export const getPendingSuggestedElementsById = (state: AppState): Map<string, NamedElementRecord> =>
  state.namedElements.suggestedElements?.pending ?? noResult;

export const getPendingSuggestedElements = createSelector(getPendingSuggestedElementsById, byId => [
  ...byId.values(),
]);

export const getSuggestedElementsMatcher = createSelector(
  getPendingSuggestedElementsById,
  suggestedElementsById => new NamedElementMatcher(suggestedElementsById),
);
